import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  FormControlLabel, LinearProgress, Stack, Switch, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppDialogTitle } from '../common/AppDialogTitle';
import { apiClient } from '../../api/client';
import { listExportablePages, type ExportablePage } from '../../export/pageEndpoints';
import { collectSnapshot, buildPayload, type GetFn } from '../../export/snapshot';
import { downloadHtml, exportFilename, fetchTemplate, fillTemplate } from '../../export/htmlBuilder';

export interface ExportDeps {
  get: GetFn;
  fetchTemplate: () => Promise<string>;
  download: (html: string, filename: string) => void;
  version: string;
}

const defaultDeps = (): ExportDeps => ({
  get: (p, q) => apiClient.get(p, q),
  fetchTemplate: () => fetchTemplate(),
  download: downloadHtml,
  version: __APP_VERSION__,
});

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  deps?: ExportDeps;
}

const keysOf = (p: ExportablePage): string[] => [p.key, ...(p.children ?? []).flatMap(keysOf)];

export function ExportDialog({ open, onClose, projectId, projectName, deps }: Props) {
  const { t } = useTranslation('projects');
  const groups = useMemo(() => listExportablePages(), []);
  const allKeys = useMemo(() => groups.flatMap((g) => g.items.flatMap(keysOf)), [groups]);
  const [whole, setWhole] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<string[] | null>(null);
  const [finished, setFinished] = useState(false);

  const pages = whole ? allKeys : allKeys.filter((k) => selected.has(k));

  const toggle = (keys: string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (on) next.add(k);
        else next.delete(k);
      }
      return next;
    });

  const label = (key: string, labelKey: string) => t(labelKey, key);

  const run = async () => {
    const d = deps ?? defaultDeps();
    setBusy(true);
    setError(null);
    setMissing(null);
    setFinished(false);
    setProgress({ done: 0, total: 0 });
    try {
      const { entries, missing: miss } = await collectSnapshot({
        projectId,
        pages,
        get: d.get,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      const payload = buildPayload({ projectId, projectName, pages, entries, version: d.version });
      const html = fillTemplate(await d.fetchTemplate(), payload);
      d.download(html, exportFilename(projectName, new Date()));
      setMissing(miss);
      setFinished(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('export.unknownError', 'Der Export ist fehlgeschlagen.'));
    } finally {
      setBusy(false);
    }
  };

  const renderPage = (p: ExportablePage, level: number) => (
    <Box key={p.key} sx={{ pl: level * 3 }}>
      <FormControlLabel
        control={<Checkbox size="small" checked={selected.has(p.key)} disabled={whole || busy} onChange={(e) => toggle([p.key], e.target.checked)} />}
        label={label(p.key, p.labelKey)}
      />
      {p.children?.map((c) => renderPage(c, level + 1))}
    </Box>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <AppDialogTitle>{t('export.title', 'Als Clickdummy exportieren')}</AppDialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'export.hint',
              'Die Daten der gewählten Seiten werden in eine einzelne HTML-Datei eingebettet, die ohne Anmeldung und ohne Server läuft. Änderungen im Clickdummy werden nicht gespeichert.',
            )}
          </Typography>
          <FormControlLabel
            control={<Switch checked={whole} disabled={busy} onChange={(e) => setWhole(e.target.checked)} />}
            label={t('export.wholeProject', 'Ganzes Projekt')}
          />
          {groups.map((g) => (
            <Box key={g.key}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    disabled={whole || busy}
                    checked={g.items.flatMap(keysOf).every((k) => whole || selected.has(k))}
                    onChange={(e) => toggle(g.items.flatMap(keysOf), e.target.checked)}
                  />
                }
                label={<Typography fontWeight={600}>{t(g.labelKey, g.key)}</Typography>}
              />
              {g.items.map((p) => renderPage(p, 1))}
            </Box>
          ))}
          <Typography variant="body2">
            {t('export.pageCount', '{{count}} Seiten ausgewählt', { count: pages.length }).replace('{{count}}', String(pages.length))}
          </Typography>
          {busy && (
            <Box>
              <LinearProgress
                variant={progress && progress.total > 0 ? 'determinate' : 'indeterminate'}
                value={progress && progress.total > 0 ? (progress.done / progress.total) * 100 : 0}
              />
              <Typography variant="caption">
                {t('export.collecting', 'Daten werden gesammelt …')} {progress ? `${progress.done}/${progress.total}` : ''}
              </Typography>
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {finished && !error && (
            <Alert severity={missing && missing.length ? 'warning' : 'success'}>
              {missing && missing.length
                ? `${missing.length} ${t('export.missing', 'Abrufe konnten nicht geladen werden')}: ${missing.join(', ')}`
                : t('export.done', 'Export abgeschlossen. Die Datei wurde heruntergeladen.')}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('actions.close', 'Schließen')}</Button>
        <Button
          variant="contained"
          onClick={run}
          disabled={busy || pages.length === 0}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
        >
          {t('export.run', 'Exportieren')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ExportDialog;
